import React, { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  ArrowUpRight,
  BrainCircuit,
  Boxes,
  Smartphone,
  Workflow,
  Handshake,
} from 'lucide-react';
import { Section, SectionHeading } from '@/components/layout/Section';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { CONSULTATION_PATH } from '@/lib/site';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const ICONS = { BrainCircuit, Boxes, Smartphone, Workflow };

/* -------------------------------------------------------------- reference */

const ReferenceCard = ({ reference, index, liveLabel }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  // Staggered drift: each card travels a little differently, so the row reads
  // as three layers rather than one flat strip.
  const depth = 1 + index * 0.35;
  const y = useTransform(scrollYProgress, [0, 1], [22 * depth, -22 * depth]);

  return (
    <motion.a
      ref={ref}
      href={reference.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`partnership-reference-${index}-link`}
      style={
        reduce
          ? { backgroundColor: 'var(--rmh-surface)' }
          : { y, backgroundColor: 'var(--rmh-surface)' }
      }
      className={cn(
        'group flex h-full flex-col justify-between overflow-hidden rounded-[16px] border p-6 lg:p-7',
        'border-[color:var(--rmh-border)] transition-[border-color,box-shadow] duration-500',
        'hover:border-[color:var(--rmh-border-strong)] focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)] focus-visible:ring-offset-2',
        !reduce && 'rmh-willchange',
      )}
    >
      <div>
        <p className="rmh-eyebrow text-[0.54rem]">{reference.tag}</p>
        <p className="mt-4 font-serif text-[1.18rem] text-[color:var(--rmh-ink)] lg:text-[1.28rem]">
          {reference.name}
        </p>
        <p className="rmh-body mt-3 text-[0.855rem] leading-[1.55]">
          {reference.body}
        </p>
      </div>
      <span className="mt-7 inline-flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.16em] text-[color:var(--rmh-ink)]">
        <span className="rmh-link-underline">{liveLabel}</span>
        <ArrowUpRight
          className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          aria-hidden="true"
        />
      </span>
    </motion.a>
  );
};

/* ---------------------------------------------------------------- section */

/**
 * The technology partnership: everything that needs engineering is delivered
 * together with CloudService.hr. Named openly, because the live products are
 * the strongest proof we have.
 */
export const PartnershipSection = () => {
  const { t } = useI18n();
  const p = t.partnership;
  const reduce = useReducedMotion();

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const washY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <Section
      id="partnership"
      tone="canvas2"
      wide
      data-testid="partnership-section"
      className="relative overflow-hidden"
    >
      <motion.span
        aria-hidden="true"
        style={reduce ? undefined : { y: washY }}
        className="rmh-champagne-wash pointer-events-none absolute -inset-y-24 inset-x-0"
      />

      <div ref={sectionRef} className="relative">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* ------------------------------------------- narrative ------- */}
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                eyebrow={p.eyebrow}
                title={p.title}
                lede={p.lede}
              />
            </Reveal>

            <Reveal delay={0.08}>
              <a
                href={p.partnerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partnership-partner-link"
                className="group mt-10 block rounded-[16px] border p-6 transition-[border-color,box-shadow] duration-500 hover:border-[color:var(--rmh-border-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--rmh-accent)] focus-visible:ring-offset-2"
                style={{
                  backgroundColor: 'var(--rmh-surface)',
                  borderColor: 'var(--rmh-border)',
                  boxShadow: 'var(--shadow-hairline)',
                }}
              >
                <div className="flex items-center gap-3">
                  <Handshake
                    className="h-4 w-4 shrink-0"
                    strokeWidth={1.5}
                    style={{ color: 'var(--rmh-accent-2)' }}
                    aria-hidden="true"
                  />
                  <p className="rmh-eyebrow text-[0.54rem]">{p.partnerLabel}</p>
                </div>
                <p className="mt-4 font-serif text-[1.3rem] text-[color:var(--rmh-ink)]">
                  {p.partnerName}
                </p>
                <p className="rmh-num mt-2.5 text-[0.66rem] tracking-[0.1em] text-[color:var(--rmh-muted-2)]">
                  {p.partnerTagline}
                </p>
                <p className="rmh-body mt-4 text-[0.875rem] italic">
                  {p.partnerClaim}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.16em] text-[color:var(--rmh-ink)]">
                  <span className="rmh-link-underline">{p.visitPartner}</span>
                  <ArrowUpRight
                    className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    aria-hidden="true"
                  />
                </span>
              </a>
            </Reveal>
          </div>

          {/* ------------------------------------------ capabilities ------ */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {p.capabilities.map((cap, i) => {
                const Icon = ICONS[cap.icon] || Boxes;
                return (
                  <Reveal key={cap.title} delay={0.05 + i * 0.07} className="h-full">
                    <div
                      data-testid={`partnership-capability-${i}`}
                      className="h-full rounded-[16px] border p-6"
                      style={{
                        backgroundColor: 'var(--rmh-surface)',
                        borderColor: 'var(--rmh-border)',
                        boxShadow: 'var(--shadow-hairline)',
                      }}
                    >
                      <span
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border"
                        style={{
                          backgroundColor: 'var(--rmh-surface-inset)',
                          borderColor: 'var(--rmh-border)',
                        }}
                      >
                        <Icon
                          className="h-[18px] w-[18px]"
                          strokeWidth={1.4}
                          style={{ color: 'var(--rmh-mocha)' }}
                          aria-hidden="true"
                        />
                      </span>
                      <p className="mt-5 font-serif text-[1.05rem] text-[color:var(--rmh-ink)]">
                        {cap.title}
                      </p>
                      <p className="rmh-body mt-2.5 text-[0.855rem] leading-[1.55]">
                        {cap.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <Reveal delay={0.1}>
              <p className="rmh-body mt-8 max-w-2xl text-[0.875rem]">{p.note}</p>
            </Reveal>
          </div>
        </div>

        {/* ------------------------------------------- live references ---- */}
        <div className="mt-16 lg:mt-20">
          <Reveal>
            <div className="flex items-center gap-3">
              <span
                className="block h-px w-10"
                style={{ backgroundColor: 'var(--rmh-accent)' }}
                aria-hidden="true"
              />
              <span className="rmh-eyebrow">{p.referencesLabel}</span>
            </div>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6">
            {p.references.map((reference, i) => (
              <Reveal key={reference.name} delay={0.05 + i * 0.08} className="h-full">
                <ReferenceCard
                  reference={reference}
                  index={i}
                  liveLabel={p.liveLabel}
                />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.12}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <CtaButton
                to={CONSULTATION_PATH}
                variant="primary"
                size="lg"
                arrow
                testId="partnership-cta-button"
              >
                {p.cta}
              </CtaButton>
              <a
                href={p.partnerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="partnership-secondary-partner-link"
                className="rmh-link-underline inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.16em] text-[color:var(--rmh-muted)]"
              >
                {p.visitPartner}
                <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
};

export default PartnershipSection;
